import { GearIcon } from '@radix-ui/react-icons';
import { ReactNode, useEffect, useState } from 'react';

import { Button } from '../../../primitives/button';
import { Sidebar, SidebarContent, SidebarProvider } from '../../../primitives/sidebar';
import { ArrowRightToLineIcon } from '../../../svg/arrow-right-to-line-icon';
import { GlobeIcon } from '../../../svg/globe-icon';
import { cn } from '../../../utils/cn';

type EditorEmailLayoutProps = {
  editorContent: ReactNode;
  emptyContent: ReactNode;
  mainContent: ReactNode;
  treeContent: ReactNode;
  selectedBlockId: string | null;
  toolbarContent?: ReactNode;
  onSidebarToggle?: () => void;
  onGlobalStylesClick?: () => void;
  isGlobalStylesSelected?: boolean;
};

export function EditorEmailLayout({
  editorContent,
  emptyContent,
  mainContent,
  treeContent,
  selectedBlockId,
  toolbarContent,
  onSidebarToggle,
  onGlobalStylesClick,
  isGlobalStylesSelected = false,
}: EditorEmailLayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);

  useEffect(() => {
    if (selectedBlockId) {
      setIsSidebarOpen(true);
    }
  }, [selectedBlockId]);

  return (
    <SidebarProvider className="h-full min-h-0!">
      <div className="flex h-full min-h-0 w-full items-stretch gap-6">
        {treeContent}
        <div className="flex min-w-0 flex-1 flex-col">
          <div>{mainContent}</div>
        </div>

        <div
          className={cn(
            'sticky top-6 translate-x-0 self-start opacity-100 transition-[width,opacity,transform,translate,scale,rotate] duration-300 ease-in-out',
            {
              'w-[25rem]': isSidebarOpen,
              'w-[3.9rem]': !isSidebarOpen,
            },
          )}
        >
          <Sidebar
            side="right"
            variant="sidebar"
            collapsible="none"
            className={cn('max-h-[calc(90svh-3rem)] w-full border bg-card', {
              'pointer-events-auto rounded-lg': isSidebarOpen,
              'rounded-full': !isSidebarOpen,
            })}
          >
            <div
              className={cn('flex flex-wrap justify-between gap-2 p-3', {
                'rounded-full': !isSidebarOpen,
                'rounded-t-lg shadow-sm': isSidebarOpen,
              })}
            >
              <div className="flex gap-2">
                <Button
                  aria-label={
                    isSidebarOpen ? 'Cerrar panel de configuración' : 'Abrir panel de configuración'
                  }
                  aria-expanded={isSidebarOpen}
                  title="Configurar elemento"
                  className="shrink-0"
                  size="icon"
                  variant="secondary"
                  onClick={() => {
                    setIsSidebarOpen((open) => !open);
                    onSidebarToggle?.();
                  }}
                >
                  {isSidebarOpen ? <ArrowRightToLineIcon /> : <GearIcon />}
                </Button>
                {isSidebarOpen && onGlobalStylesClick && (
                  <Button
                    aria-label="Estilos globales"
                    aria-pressed={isGlobalStylesSelected}
                    className="shrink-0"
                    size="icon"
                    title="Estilos globales"
                    variant={isGlobalStylesSelected ? 'accent' : 'secondary'}
                    onClick={() => {
                      setIsSidebarOpen(true);
                      onGlobalStylesClick();
                    }}
                  >
                    <GlobeIcon />
                  </Button>
                )}
              </div>
              {isSidebarOpen && toolbarContent}
            </div>

            {isSidebarOpen ? (
              <>
                <SidebarContent className="h-full overflow-y-scroll pt-4 pr-2 pb-2 pl-4">
                  {selectedBlockId ? editorContent : emptyContent}
                </SidebarContent>
                <div className="h-3" />
              </>
            ) : null}
          </Sidebar>
        </div>
      </div>
    </SidebarProvider>
  );
}
